import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Seo } from "./components/Seo";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { BlogList } from "./components/BlogList";
import { BlogPost } from "./components/BlogPost";
import { BlogAdmin } from "./components/BlogAdmin";
import { 
  DollarSign, TrendingUp, ShieldCheck, BookOpen, Award, Calendar, 
  Phone, Mail, MapPin, Menu, X, ChevronRight, Download, Search, 
  Trash2, LogOut, Lock, PieChart, Briefcase, Percent, Clock, 
  ArrowRight, User, CheckCircle2, AlertCircle, FileSpreadsheet,
  ChevronDown
} from "lucide-react";

// Setup base backend and API urls
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios default to send credentials (cookies) for all requests
axios.defaults.withCredentials = true;

// Helper to format API error message strings
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Generate next 4 upcoming Saturdays dynamically
const getNextSaturdays = () => {
  const dates = [];
  const today = new Date();
  const day = today.getDay();
  
  // 6 corresponds to Saturday. Calculate distance to first upcoming Saturday
  let diff = (6 - day + 7) % 7;
  if (diff === 0) diff = 7; // If today is Saturday, offer next Saturday
  
  const firstSat = new Date(today);
  firstSat.setDate(today.getDate() + diff);
  
  for (let i = 0; i < 4; i++) {
    const nextSat = new Date(firstSat);
    nextSat.setDate(firstSat.getDate() + (i * 7));
    const formattedDate = nextSat.toLocaleDateString("en-US", { 
      weekday: "long", 
      month: "long", 
      day: "numeric", 
      year: "numeric" 
    });
    dates.push(formattedDate);
  }
  return dates;
};

// Curriculum content array for the text-based training
const CURRICULUM_TOPICS = [
  {
    title: "1. Core Financial Planning",
    icon: <Briefcase className="h-6 w-6 text-[#D4AF37]" />,
    description: "Understand how to build a unified financial strategy. Align your income, liabilities, assets, and future goals into a singular, executable roadmap.",
    details: "Learn the 5 pillars of financial structure, how to benchmark your current net worth, and how inflation affects your purchasing power over a 10, 20, and 30-year horizon."
  },
  {
    title: "2. Master Budgeting & Cash Flow",
    icon: <PieChart className="h-6 w-6 text-[#D4AF37]" />,
    description: "Take absolute control of your cash flow. Track your inflows and outflows with modular, friction-free rules that preserve capital.",
    details: "Discover the 50/30/20 cash allocation framework, how to identify hidden leaks in recurring subscriptions, and how to optimize monthly savings without sacrificing your lifestyle."
  },
  {
    title: "3. Creating an Emergency Fund",
    icon: <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />,
    description: "Establish a financial fortress. Insulate your long-term investments from unexpected short-term liquidity shocks.",
    details: "Learn how to calculate your true liquid runway, the safest high-yield accounts for emergency cash, and exactly when (and when not) to deploy these critical reserves."
  },
  {
    title: "4. Risk Management & Insurance",
    icon: <Percent className="h-6 w-6 text-[#D4AF37]" />,
    description: "Protect what you build. Shield your family and assets against unforeseen catastrophic medical or liability events.",
    details: "Analyze the core differences between Term and Whole Life insurance, learn how to calculate optimal health coverage limits, and avoid high-commission toxic insurance products."
  },
  {
    title: "5. Mutual Funds Decoded",
    icon: <BookOpen className="h-6 w-6 text-[#D4AF37]" />,
    description: "Harness the power of diversification. Discover how pooled investments can generate compounding, passive capital growth.",
    details: "Understand Large, Mid, and Small-cap classifications, how to evaluate expense ratios and historical fund manager performance, and the massive benefits of Systematic Investment Plans (SIPs)."
  },
  {
    title: "6. Strategic Stock Investing",
    icon: <TrendingUp className="h-6 w-6 text-[#D4AF37]" />,
    description: "Direct equity market mastery. Learn how to identify, evaluate, and invest in high-quality public corporations for long-term compounding.",
    details: "Master fundamental analysis metrics (P/E ratios, ROE, debt-to-equity), distinction between value and growth stocks, and building a low-stress core equity portfolio."
  },
  {
    title: "7. Retirement Planning",
    icon: <Clock className="h-6 w-6 text-[#D4AF37]" />,
    description: "Secure your financial freedom. Design a customized post-career nesting egg that ensures you never outlive your savings.",
    details: "Learn the '4% Safe Withdrawal Rule', how to combat retirement inflation, optimize government-sponsored pension schemes, and establish stable dual passive cash flows."
  },
  {
    title: "8. Tactical Tax Planning",
    icon: <DollarSign className="h-6 w-6 text-[#D4AF37]" />,
    description: "Maximize your post-tax yields. Structure your investments, income, and deductions legally to minimize wealth dilution.",
    details: "Analyze current tax-saving instruments, learn strategies to defer or avoid Capital Gains tax on equities and property, and plan long-term tax-efficient withdrawals."
  },
  {
    title: "9. Generational Wealth Creation",
    icon: <Award className="h-6 w-6 text-[#D4AF37]" />,
    description: "Build an enduring legacy. Establish multi-generational asset transfers that empower your descendants with robust financial security.",
    details: "Explore estate planning fundamentals, the role of trusts and wills, and how compounding works across 50+ year cycles to create legendary family foundations."
  }
];

// Header / Navigation Component
const Navbar = ({ isLoggedIn, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isAdminPage = location.pathname === "/admin";

  const handleScroll = (id) => {
    setIsOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      // Not on the landing page — go home and scroll to the section
      window.location.href = `/#${id}`;
    }
  };

  return (
    <>
      {/* Slim top strip — ARN disclosure */}
      <div className="bg-[#1E3A8A] text-white text-xs sm:text-[13px] py-2 px-6 text-center tracking-wide font-medium border-b border-[#D4AF37]/40" data-testid="arn-top-strip">
        <span className="text-[#F2C94C] font-semibold">AMFI-Registered Mutual Fund Distributor</span>
        <span className="mx-2 text-slate-300">·</span><span className="font-semibold">ARN-107338</span>
        <span className="mx-2 text-slate-300 hidden sm:inline">·</span>
        <span className="hidden sm:inline text-slate-200">Educational Training Platform</span>
      </div>
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo / Brand */}
          <Link 
            to="/" 
            data-testid="navbar-brand-link" 
            className="flex items-center space-x-3 text-[#1E3A8A] font-semibold text-lg sm:text-xl tracking-tight"
          >
            <div className="bg-[#1E3A8A] text-white p-2 rounded-lg flex items-center justify-center shadow-md">
              <TrendingUp className="h-5 w-5 stroke-[2] text-[#D4AF37]" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold leading-tight">Ashish Ranade</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Wealth Educator</span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center space-x-8">
            {!isAdminPage ? (
              <>
                <button 
                  onClick={() => handleScroll("about")} 
                  data-testid="nav-link-about"
                  className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors text-sm"
                >
                  About
                </button>
                <button 
                  onClick={() => handleScroll("training")} 
                  data-testid="nav-link-training"
                  className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors text-sm"
                >
                  Training
                </button>
                <button 
                  onClick={() => handleScroll("why-meet")} 
                  data-testid="nav-link-why-meet-me"
                  className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors text-sm"
                >
                  Why Meet Me
                </button>
                <Link
                  to="/blog"
                  data-testid="nav-link-blog"
                  className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors text-sm"
                >
                  Blog
                </Link>
                <button 
                  onClick={() => handleScroll("book")} 
                  data-testid="nav-link-contact"
                  className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-5 py-2.5 rounded-lg text-xs font-semibold uppercase tracking-wider shadow-md transition-all hover:-translate-y-0.5"
                >
                  Book Free Session
                </button>
              </>
            ) : (
              <Link 
                to="/" 
                data-testid="nav-link-home"
                className="text-slate-600 hover:text-[#1E3A8A] font-medium transition-colors text-sm flex items-center space-x-1"
              >
                <span>← Back to Website</span>
              </Link>
            )}

            {isLoggedIn ? (
              <div className="flex items-center space-x-4 pl-4 border-l border-slate-200">
                <Link 
                  to="/admin" 
                  data-testid="nav-link-admin"
                  className="text-xs bg-[#D4AF37] hover:bg-[#B4952F] text-white px-4 py-2 rounded-lg font-semibold uppercase tracking-wider transition-colors"
                >
                  Dashboard
                </Link>
                <button 
                  onClick={onLogout} 
                  data-testid="logout-button"
                  className="text-slate-600 hover:text-red-600 transition-colors"
                  title="Logout Admin"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <Link 
                to="/admin" 
                data-testid="nav-link-admin"
                className="text-slate-500 hover:text-[#1E3A8A] transition-colors"
                title="Admin Control"
              >
                <Lock className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            {isLoggedIn && (
              <button 
                onClick={onLogout} 
                data-testid="logout-button-mobile"
                className="text-slate-600 hover:text-red-600"
              >
                <LogOut className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-slate-600 hover:text-[#1E3A8A] focus:outline-none p-1"
              aria-label="Toggle Menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-b border-slate-100 px-6 py-6 space-y-4">
          {!isAdminPage ? (
            <>
              <button
                onClick={() => handleScroll("about")}
                data-testid="nav-link-about-mobile"
                className="block w-full text-left text-slate-600 hover:text-[#1E3A8A] font-semibold py-2"
              >
                About Ashish
              </button>
              <button
                onClick={() => handleScroll("training")}
                data-testid="nav-link-training-mobile"
                className="block w-full text-left text-slate-600 hover:text-[#1E3A8A] font-semibold py-2"
              >
                What You&apos;ll Learn
              </button>
              <button
                onClick={() => handleScroll("why-meet")}
                data-testid="nav-link-why-meet-me-mobile"
                className="block w-full text-left text-slate-600 hover:text-[#1E3A8A] font-semibold py-2"
              >
                Why Saturday Session?
              </button>
              <Link
                to="/blog"
                onClick={() => setIsOpen(false)}
                data-testid="nav-link-blog-mobile"
                className="block w-full text-left text-slate-600 hover:text-[#1E3A8A] font-semibold py-2"
              >
                Blog
              </Link>
              <button
                onClick={() => handleScroll("book")}
                data-testid="nav-link-contact-mobile"
                className="block w-full text-center bg-[#1E3A8A] text-white py-3 rounded-lg font-semibold tracking-wide"
              >
                Book Your Saturday Session
              </button>
            </>
          ) : (
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="block w-full text-left text-slate-600 hover:text-[#1E3A8A] font-semibold py-2"
            >
              ← Back to Website
            </Link>
          )}

          <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-400">Admin Controls</span>
            <Link
              to="/admin"
              onClick={() => setIsOpen(false)}
              data-testid="nav-link-admin-mobile"
              className="text-sm text-[#1E3A8A] hover:text-[#D4AF37] font-semibold flex items-center space-x-1"
            >
              <Lock className="h-3.5 w-3.5 mr-1" />
              <span>{isLoggedIn ? "Go to Dashboard" : "Admin Login"}</span>
            </Link>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

// Website Landing Page
const LandingPage = ({ onScrollToForm }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  
  const upcomingSaturdays = getNextSaturdays();

  // Scroll to a section if the URL has a hash (e.g. coming from /blog via /#book)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...formData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess(false);

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source: "website_lead_form"
      });
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        preferred_date: "",
        message: ""
      });
    } catch (err) {
      console.error(err);
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit application. Please check your network and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScrollToForm = (sourceName = "generic_cta") => {
    setFormData(prev => ({
      ...prev,
      message: `Hi Ashish, I am interested in the Saturday Online Session. Source: ${sourceName}`
    }));
    const element = document.getElementById("book");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="bg-white min-h-screen text-slate-800">
      <Seo
        title="Ashish Ranade – Free Financial Planning & Wealth Education Training"
        description="Join Ashish Ranade's FREE wealth education & financial planning training. Book a free Saturday online session for conflict-free guidance on budgeting, mutual funds, insurance, tax and retirement."
        canonical="https://ashishranade.com/"
      />
      
      {/* 1. Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-24 md:py-32 border-b border-slate-100 bg-white">
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1765046255462-198d49d07dd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGZpbmFuY2UlMjBncm93dGglMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgyNzQ2MjIzfDA&ixlib=rb-4.1.0&q=85')` }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-8 text-left">
              <div className="inline-flex items-center space-x-2 bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 px-3.5 py-1.5 rounded-full text-xs font-semibold text-[#1E3A8A] tracking-wider uppercase">
                <span className="h-2 w-2 rounded-full bg-[#D4AF37] animate-pulse"></span>
                <span>EXCLUSIVE SATURDAY ONLINE SESSION</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1E3A8A] leading-tight">
                Master Your Money. <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#D4AF37]">
                  Secure Your Wealth.
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl font-light">
                Get high-authority, institutional-grade <span className="font-semibold text-slate-800">FREE Wealth Education & Financial Planning Training</span>. 
                Walk away with complete clarity and claim your personalized roadmap at a 1-on-1 Saturday Online Consultation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleScrollToForm("Hero CTA Button")}
                  data-testid="hero-cta-button"
                  className="bg-[#D4AF37] hover:bg-[#B4952F] text-white px-8 py-4 rounded-xl font-semibold shadow-[0_10px_25px_rgba(212,175,55,0.25)] transition-all hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(212,175,55,0.35)] flex items-center justify-center space-x-2"
                >
                  <span>Book Free Saturday Session</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <a
                  href="#training"
                  className="border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 text-[#1E3A8A] px-8 py-4 rounded-xl font-semibold transition-all hover:-translate-y-0.5 text-center flex items-center justify-center"
                >
                  Explore Free Curriculum
                </a>
              </div>

              {/* Direct Quick Stats / Trust Builders */}
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-slate-100 max-w-xl">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#1E3A8A]">100%</div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1">Free Education</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#1E3A8A]">1-on-1</div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1">Personal Focus</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-[#D4AF37]">Premium</div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1">Gold Accents</div>
                </div>
              </div>
            </div>

            {/* Right Interactive Card / Visual */}
            <div className="lg:col-span-5 relative">
              <div className="absolute inset-0 bg-[#D4AF37]/5 rounded-3xl blur-2xl -z-10 transform translate-x-4 translate-y-4"></div>
              <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.06)] space-y-6 text-left relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-gradient-to-bl from-[#D4AF37] to-transparent w-24 h-24 opacity-10 rounded-tr-3xl"></div>
                <div className="flex items-center space-x-4">
                  <div className="bg-[#1E3A8A]/10 text-[#1E3A8A] p-3 rounded-2xl">
                    <Calendar className="h-6 w-6 stroke-[1.5]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Saturday Online Session</h3>
                    <p className="text-sm text-slate-500">Live online — join from anywhere in India</p>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Every Saturday, Ashish hosts private wealth strategy deep-dives. Bring your questions, bank statements, mutual fund portfolio and design a clear, custom wealth blueprint.
                </p>
                <ul className="space-y-3.5">
                  {[
                    "Personalized 1-on-1 attention",
                    "Deep-dive portfolio & liability analysis",
                    "Budgeting & tax optimization advice",
                    "Absolutely FREE. Zero obligations."
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-[#D4AF37] shrink-0 mt-0.5 mr-2.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleScrollToForm("Hero Card CTA")}
                  className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center space-x-2"
                >
                  <span>Select Preferred Saturday</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. About Section */}
      <section id="about" className="py-24 bg-slate-50/50 text-left border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Portrait */}
            <div className="lg:col-span-5 relative">
              <div className="absolute inset-0 bg-[#D4AF37]/10 rounded-2xl transform -rotate-3 scale-[1.02]"></div>
              <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-xl aspect-[4/5] bg-slate-200">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/b5583ce3-5c70-446b-87a4-88291b966ba2/images/1435aa9c054190e163376daa9a4b16a747c407991832a161e1ccda7ebb199cad.jpeg" 
                  alt="Ashish Ranade Financial Expert"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white border border-slate-100 p-5 rounded-xl shadow-lg flex items-center space-x-4 max-w-xs">
                <div className="bg-[#D4AF37] text-white p-2.5 rounded-lg flex items-center justify-center">
                  <Award className="h-6 w-6" />
                </div>
                <div>
                  <div className="font-bold text-[#1E3A8A] text-sm">Ashish Ranade</div>
                  <div className="text-xs text-slate-500 font-medium">Wealth Educator</div>
                </div>
              </div>
            </div>

            {/* Right Column: Narrative */}
            <div className="lg:col-span-7 space-y-6">
              <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider">FOUNDER PORTRAIT</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0F172A]">
                Meet Ashish Ranade
              </h2>
              <div className="h-1 w-16 bg-[#D4AF37] rounded"></div>
              <p className="text-slate-600 leading-relaxed font-light text-base sm:text-lg">
                For over two decades, Ashish Ranade has helped families, professionals, and corporate leaders construct bulletproof financial strategies. Believing that financial literacy is the ultimate vehicle for personal liberation, Ashish has dedicated his Saturday online sessions to training, guiding, and mentoring wealth builders absolutely free of charge.
              </p>
              <p className="text-slate-600 leading-relaxed font-light text-base">
                Rather than offering complex product pitches or hard-sell investment lists, Ashish offers foundational financial concepts tailored strictly to your lifecycle. Through highly pedagogical Saturday online sessions, he ensures you master the exact framework needed to build, manage, protect, and multiply your generational capital.
              </p>
              <p className="text-sm text-slate-400 font-light leading-relaxed">
                Ashish is also an AMFI-registered Mutual Fund Distributor (ARN-107338). This website, however, is dedicated purely to education — any mutual fund assistance is entirely optional and only on request.
              </p>

              {/* Core Philosophy Bullets */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-start">
                  <div className="bg-[#1E3A8A]/5 p-2 rounded-lg mr-3">
                    <CheckCircle2 className="h-5 w-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">Conflict-Free Guidance</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Education-first sessions focused on learning — never pushy product sales or pressure.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#1E3A8A]/5 p-2 rounded-lg mr-3">
                    <CheckCircle2 className="h-5 w-5 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">1-on-1 Strategy Focus</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Custom wealth structures evaluated against your personal tax bracket and lifecycle.</p>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <button
                  onClick={() => handleScrollToForm("About Section CTA")}
                  className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-semibold px-6 py-3 rounded-lg text-sm transition-all"
                >
                  Schedule Your Free Strategy Session
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. What You'll Learn (Curriculum) */}
      <section id="training" className="py-24 bg-white border-b border-slate-100 text-left">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
            <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider">FREE FINANCIAL TRAINING</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0F172A]">
              Complete Wealth & Financial Curriculum
            </h2>
            <div className="h-1 w-20 bg-[#D4AF37] mx-auto rounded"></div>
            <p className="text-slate-500 font-light text-base leading-relaxed">
              Explore the comprehensive, structured financial syllabus taught by Ashish Ranade. Master each concept, apply the frameworks to your household ledger, and fast-track your route to complete financial sovereignty.
            </p>
          </div>

          {/* Curriculum Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CURRICULUM_TOPICS.map((topic, index) => (
              <div 
                key={index} 
                className="bg-white border border-slate-100 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_15px_40px_rgba(0,0,0,0.06)] hover:border-slate-200/80 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  {/* Top: Icon + Title */}
                  <div className="flex items-center space-x-3 pb-3 border-b border-slate-50">
                    <div className="bg-[#1E3A8A]/5 p-2.5 rounded-xl">
                      {topic.icon}
                    </div>
                    <h3 className="font-bold text-[#1E3A8A] text-base leading-snug sm:text-lg">
                      {topic.title}
                    </h3>
                  </div>

                  {/* Core description */}
                  <p className="text-sm font-medium text-slate-800 leading-relaxed">
                    {topic.description}
                  </p>

                  {/* Syllabus / deep details */}
                  <p className="text-xs text-slate-500 leading-relaxed font-light bg-slate-50 p-3 rounded-lg border border-slate-100/50">
                    <span className="font-semibold text-slate-700 block mb-1">Syllabus Highlights:</span>
                    {topic.details}
                  </p>
                </div>

                {/* Bottom Card CTA */}
                <div className="pt-6 mt-4 border-t border-slate-50">
                  <button
                    onClick={() => handleScrollToForm(`Topic ${index + 1}: ${topic.title}`)}
                    data-testid={`curriculum-card-cta-${index}`}
                    className="w-full text-left inline-flex items-center justify-between text-xs font-bold text-[#D4AF37] hover:text-[#B4952F] group transition-colors"
                  >
                    <span className="max-w-[85%] leading-relaxed">Book Your FREE Saturday Online Financial Planning Session for a personalized wealth plan.</span>
                    <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1.5 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 4. Why Meet Me Section */}
      <section id="why-meet" className="py-24 bg-slate-50/50 text-left border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6">
              <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider">WHY WORK WITH ASHISH</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0F172A]">
                The Saturday Online Session Difference
              </h2>
              <div className="h-1 w-16 bg-[#D4AF37] rounded"></div>
              <p className="text-slate-600 font-light leading-relaxed">
                Most financial services are designed to sell you mutual funds, high-premium insurance policies, or complex structured notes. They thrive on confusion. Ashish Ranade takes the exact opposite approach. Our objective is educational empowerment and complete conflict-free strategy.
              </p>

              <div className="space-y-4 pt-2">
                {[
                  {
                    title: "Personalized Wealth Assessment",
                    desc: "Not a template or computerized report. Ashish personally inspects your debt structure, tax tier, life targets, and designs a manual roadmap."
                  },
                  {
                    title: "Completely Conflict-Free",
                    desc: "We do not touch your investments. We are educational partners. Your wealth remains under your control at all times."
                  },
                  {
                    title: "Focused Live Online Consultation",
                    desc: "Join a private video session with Ashish from the comfort of your home. Share your screen, ask anything, and solve complex asset allocation problems together in real time."
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white border border-slate-100 p-5 rounded-xl shadow-sm flex items-start space-x-4">
                    <div className="bg-[#D4AF37]/10 text-[#D4AF37] p-2 rounded-lg flex items-center justify-center font-bold">
                      0{idx + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1E3A8A] text-sm sm:text-base">{item.title}</h4>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1 font-light leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image / Callout Column */}
            <div className="lg:col-span-5 space-y-8 relative">
              <div className="absolute inset-0 bg-[#1E3A8A]/5 rounded-3xl blur-3xl -z-10"></div>
              <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nJTIwbGlnaHwlMjBicmlnaHR8ZW58MHx8fHwxNzgyNzQ2MjIzfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Online consultation Saturday Session"
                  className="w-full h-56 object-cover"
                />
                <div className="p-6 space-y-4">
                  <div className="flex items-center space-x-2 text-[#D4AF37]">
                    <Clock className="h-5 w-5" />
                    <span className="font-bold text-xs uppercase tracking-wider">LIMITED PRIVATE BOOKINGS</span>
                  </div>
                  <h4 className="font-bold text-slate-900 text-lg">Saturday Online Strategy Sessions</h4>
                  <p className="text-sm text-slate-500 leading-relaxed font-light">
                    Due to high demand and strict personal 1-on-1 mentorship limits, Saturday sessions are allocated strictly in advance. Ensure you reserve your block to secure focused planning time with Ashish.
                  </p>
                  <button
                    onClick={() => handleScrollToForm("Why Meet Me CTA")}
                    className="w-full text-center bg-[#D4AF37] hover:bg-[#B4952F] text-white py-3 rounded-lg font-semibold text-sm transition-all"
                  >
                    Check Slot Availability
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 5. Contact / Booking Form */}
      <section id="book" className="py-24 bg-white text-left">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          
          <div className="bg-white border border-slate-100 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="bg-[#1E3A8A] text-white px-8 py-10 md:p-12 text-center space-y-4 relative">
              <div className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-overlay" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nJTIwbGlnaHwlMjBicmlnaHR8ZW58MHx8fHwxNzgyNzQ2MjIzfDA&ixlib=rb-4.1.0&q=85')` }} />
              <div className="relative z-10 space-y-2">
                <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest bg-white/10 px-3.5 py-1 rounded-full">RESERVE A SLOT</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
                  Book Your Saturday Online Session
                </h2>
                <p className="text-slate-300 font-light max-w-2xl mx-auto text-sm sm:text-base">
                  Get high-authority, personalized wealth education and professional guidance. Complete this simple, safe form and secure your strategy slot with Ashish Ranade.
                </p>
              </div>
            </div>

            <div className="p-8 md:p-12">
              {success ? (
                <div 
                  data-testid="contact-success-alert"
                  className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl p-8 text-center space-y-4 max-w-lg mx-auto"
                >
                  <div className="bg-emerald-500 text-white h-12 w-12 rounded-full flex items-center justify-center mx-auto shadow-md">
                    <CheckCircle2 className="h-6 w-6 stroke-[2]" />
                  </div>
                  <h3 className="font-bold text-xl">Booking Requested Successfully!</h3>
                  <p className="text-sm font-light leading-relaxed">
                    Thank you for applying for a Free Saturday Online Session. Ashish or his personal team will contact you on your registered phone number or email within 24 hours to confirm your exact meeting time.
                  </p>
                  <button 
                    onClick={() => setSuccess(false)}
                    className="bg-[#1E3A8A] text-white font-semibold py-2.5 px-6 rounded-lg text-xs hover:bg-[#1E3A8A]/90 transition-colors"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-start space-x-3 text-sm">
                      <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Full Name</label>
                      <input 
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                        className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Email Address</label>
                      <input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="johndoe@example.com"
                        data-testid="contact-email-input"
                        className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Phone */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Phone Number (10+ digits)</label>
                      <input 
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        minLength={10}
                        placeholder="9687944888"
                        data-testid="contact-phone-input"
                        className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
                      />
                    </div>

                    {/* Preferred Date */}
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Preferred Saturday</label>
                      <div className="relative">
                        <select
                          name="preferred_date"
                          value={formData.preferred_date}
                          onChange={handleInputChange}
                          required
                          data-testid="contact-date-select"
                          className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all appearance-none text-sm pr-10"
                        >
                          <option value="">Select an upcoming Saturday</option>
                          {upcomingSaturdays.map((date, idx) => (
                            <option key={idx} value={date}>{date}</option>
                          ))}
                          <option value="Coordinate via Phone">Any Saturday (Coordinate on call)</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                          <ChevronDown className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Your Financial Profile or Questions (Optional)</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Tell Ashish a bit about your wealth goals, current portfolio size, liabilities, or specific financial bottlenecks you want resolved..."
                      data-testid="contact-message-input"
                      className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="w-full bg-[#D4AF37] hover:bg-[#B4952F] disabled:bg-[#D4AF37]/50 text-white font-semibold py-4 rounded-xl transition-all shadow-[0_8px_25px_rgba(212,175,55,0.2)] flex items-center justify-center space-x-2"
                  >
                    <span>{submitting ? "Sending Request..." : "Request Saturday Online Session"}</span>
                    {!submitting && <ArrowRight className="h-5 w-5" />}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

// Admin Login Route Component
const LoginPage = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      onLoginSuccess(response.data);
      navigate("/admin");
    } catch (err) {
      console.error(err);
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid credentials or blocked due to brute force protection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-80px)] flex items-center justify-center px-6">
      <Seo
        title="Admin Login | Ashish Ranade Wealth Educator"
        description="Secure admin login for the Ashish Ranade Wealth Educator lead and booking dashboard."
        noindex
      />
      <div className="max-w-md w-full bg-white border border-slate-100 rounded-3xl p-8 shadow-xl space-y-6 text-left relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-[#D4AF37]" />
        
        <div className="text-center space-y-2">
          <div className="bg-[#1E3A8A]/10 text-[#1E3A8A] h-12 w-12 rounded-full flex items-center justify-center mx-auto">
            <Lock className="h-6 w-6 stroke-[1.5]" />
          </div>
          <h1 className="font-bold text-2xl text-slate-900">Admin Control Room</h1>
          <p className="text-xs text-slate-500">Access limited exclusively to Ashish Ranade</p>
        </div>

        {error && (
          <div 
            data-testid="login-error-alert"
            className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-start space-x-2 text-xs"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Email Address</label>
            <input 
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              placeholder="ranadeashish1971@gmail.com"
              className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wider block">Password</label>
            <input 
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
              placeholder="••••••••"
              className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-semibold py-3.5 rounded-xl transition-all shadow-[0_8px_25px_rgba(30,58,138,0.15)] flex items-center justify-center space-x-2"
          >
            <span>{loading ? "Verifying..." : "Login Securely"}</span>
            {!loading && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analytics/summary`)
      .then((res) => setAnalytics(res.data))
      .catch(() => setAnalytics(null));
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/leads`, {
        params: {
          search: search || undefined,
          status_filter: statusFilter || undefined
        }
      });
      setLeads(response.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch leads. Please verify authorization and refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [search, statusFilter]);

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const response = await axios.put(`${API}/leads/${id}/status`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status: response.data.status } : l));
    } catch (err) {
      console.error(err);
      setError("Failed to update status. Please try again.");
    } finally {
      setUpdatingId("");
    }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead? This action is permanent.")) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error(err);
      setError("Failed to delete lead. Please try again.");
    }
  };

  const handleExportCSV = () => {
    // Navigate directly to download the leads export
    window.open(`${API}/leads/export`, "_blank");
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-80px)] py-12 text-left">
      <Seo
        title="Leads Dashboard | Ashish Ranade Admin"
        description="Manage Saturday online session bookings and financial planning leads for Ashish Ranade Wealth Educator."
        noindex
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8">
        
        {/* Header Title & Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white border border-slate-100 p-6 rounded-2xl shadow-sm">
          <div>
            <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Financial Leads & Bookings</h1>
            <p className="text-xs text-slate-500 mt-1">Manage and track your Saturday Online Session reservations</p>
          </div>
          <div className="flex items-center gap-3 shrink-0 self-start sm:self-auto">
            <Link
              to="/admin/blog"
              data-testid="admin-manage-blog-button"
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-5 py-3 rounded-xl font-semibold text-sm transition-all shadow-md flex items-center justify-center space-x-2"
            >
              <BookOpen className="h-4 w-4" />
              <span>Manage Blog</span>
            </Link>
            <button
              onClick={handleExportCSV}
              data-testid="admin-export-button"
              className="bg-[#D4AF37] hover:bg-[#B4952F] text-white px-5 py-3 rounded-xl font-semibold text-sm transition-all shadow-[0_8px_20px_rgba(212,175,55,0.15)] flex items-center justify-center space-x-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              <span>Export to CSV / Excel</span>
            </button>
          </div>
        </div>

        {/* Visitor Analytics Panel */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6 space-y-5" data-testid="admin-analytics-panel">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#1E3A8A]" />
            <h2 className="font-bold text-lg text-slate-900">Website Visitors</h2>
          </div>
          {analytics ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: "Unique Visitors", value: analytics.unique_visitors, testid: "stat-unique" },
                  { label: "Total Page Views", value: analytics.total_views, testid: "stat-total" },
                  { label: "Visitors Today", value: analytics.today_unique, testid: "stat-today-unique" },
                  { label: "Views Today", value: analytics.today_views, testid: "stat-today-views" },
                ].map((s) => (
                  <div key={s.testid} data-testid={s.testid} className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                    <div className="text-3xl font-bold text-[#1E3A8A]">{s.value}</div>
                    <div className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
              {analytics.top_pages && analytics.top_pages.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Most Visited Pages</h3>
                  <ul className="space-y-1.5">
                    {analytics.top_pages.map((p) => (
                      <li key={p.path} className="flex items-center justify-between text-sm border-b border-slate-50 pb-1.5">
                        <span className="text-slate-700 font-medium truncate mr-4">{p.path === "/" ? "/ (Home)" : p.path}</span>
                        <span className="text-slate-500 shrink-0">{p.views} views</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[11px] text-slate-400">Counts visits to your public pages (home & blog). Data starts from when this feature went live. For deeper insights (locations, devices, sources), use your Google Analytics dashboard.</p>
            </>
          ) : (
            <p className="text-sm text-slate-500" data-testid="analytics-empty">No visitor data yet — stats will appear here as people visit your site.</p>
          )}
        </div>


        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center space-x-2 text-sm">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Filters Controls Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Search bar */}
          <div className="relative md:col-span-8">
            <Search className="absolute left-4 inset-y-0 my-auto h-4 w-4 text-slate-400 pointer-events-none" />
            <input 
              type="text"
              placeholder="Search by name, email, phone, or specific questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search-input"
              className="w-full bg-white border border-slate-200 rounded-xl pl-11 pr-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all text-sm shadow-sm"
            />
          </div>

          {/* Status Select */}
          <div className="relative md:col-span-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-status-filter"
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all appearance-none text-sm pr-10 shadow-sm"
            >
              <option value="">All Lead Statuses</option>
              <option value="new">New / Unprocessed</option>
              <option value="contacted">Contacted</option>
              <option value="scheduled">Scheduled Session</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
        </div>

        {/* Lead Grid/Table */}
        <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-16 text-center space-y-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A8A] mx-auto"></div>
              <p className="text-sm text-slate-500">Retrieving secure financial records...</p>
            </div>
          ) : leads.length === 0 ? (
            <div 
              data-testid="admin-empty-state"
              className="p-16 text-center space-y-4 max-w-md mx-auto"
            >
              <div className="bg-slate-100 text-slate-400 h-16 w-16 rounded-full flex items-center justify-center mx-auto">
                <Search className="h-8 w-8" />
              </div>
              <h3 className="font-bold text-lg text-slate-800">No matching leads found</h3>
              <p className="text-sm text-slate-400 font-light">
                We couldn&apos;t find any Saturday Session bookings matching your current filter criteria.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 uppercase tracking-widest text-[10px] font-bold">
                    <th className="py-4 px-6">Client Details</th>
                    <th className="py-4 px-6">Preferred Saturday</th>
                    <th className="py-4 px-6">Question / Context</th>
                    <th className="py-4 px-6">Status</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {leads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      data-testid={`admin-lead-row-${lead.id}`}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      {/* Name & Contact */}
                      <td className="py-4 px-6 space-y-1">
                        <div className="font-bold text-slate-900 text-base">{lead.name}</div>
                        <div className="flex flex-col space-y-0.5 text-xs text-slate-500 font-light">
                          <span className="flex items-center"><Mail className="h-3 w-3 mr-1 text-slate-400" /> {lead.email}</span>
                          <span className="flex items-center"><Phone className="h-3 w-3 mr-1 text-slate-400" /> {lead.phone}</span>
                        </div>
                      </td>

                      {/* Pref Date */}
                      <td className="py-4 px-6 font-medium text-slate-700">
                        {lead.preferred_date || "Any Saturday"}
                      </td>

                      {/* Message/Questions */}
                      <td className="py-4 px-6 max-w-sm">
                        <p className="text-xs text-slate-600 line-clamp-3 font-light leading-relaxed">
                          {lead.message || <span className="italic text-slate-300">No comments provided.</span>}
                        </p>
                        <span className="inline-block bg-slate-100 text-slate-500 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold mt-1.5">
                          Src: {lead.source || "form"}
                        </span>
                      </td>

                      {/* Status select dropdown */}
                      <td className="py-4 px-6">
                        <div className="relative inline-block w-36">
                          <select
                            value={lead.status}
                            disabled={updatingId === lead.id}
                            onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                            data-testid={`admin-lead-status-select-${lead.id}`}
                            className={`w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-[#D4AF37] focus:border-transparent transition-all appearance-none pr-8 ${
                              lead.status === "new" ? "text-blue-700 bg-blue-50 border-blue-100" :
                              lead.status === "contacted" ? "text-amber-700 bg-amber-50 border-amber-100" :
                              lead.status === "scheduled" ? "text-purple-700 bg-purple-50 border-purple-100" :
                              lead.status === "completed" ? "text-emerald-700 bg-emerald-50 border-emerald-100" :
                              "text-slate-500 bg-slate-100 border-slate-200"
                            }`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-500">
                            <ChevronDown className="h-3 w-3" />
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          data-testid={`admin-lead-delete-button-${lead.id}`}
                          className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all inline-flex items-center justify-center"
                          title="Delete Reservation"
                        >
                          <Trash2 className="h-4.5 w-4.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

// ProtectedRoute Guard Component
const ProtectedRoute = ({ isLoggedIn, checkingAuth, children }) => {
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="space-y-3 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A8A] mx-auto"></div>
          <p className="text-sm text-slate-400">Verifying secure admin credentials...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Link to="/admin/login" replace />;
  }

  return children;
};

// Sends a GA4 page_view on every client-side route change (SPA navigation)
const RouteTracker = () => {
  const location = useLocation();
  const lastTracked = useRef(null);
  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    // Built-in visitor tracking (public pages only), de-duplicated per path
    if (!location.pathname.startsWith("/admin") && lastTracked.current !== location.pathname) {
      lastTracked.current = location.pathname;
      try {
        let vid = localStorage.getItem("vid");
        if (!vid) {
          vid = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
          localStorage.setItem("vid", vid);
        }
        axios.post(`${API}/track`, { path: location.pathname, visitor_id: vid }).catch(() => {});
      } catch (e) { /* ignore */ }
    }
  }, [location]);
  return null;
};

// Primary App Component
function App() {
  const [user, setUser] = useState(null); // stores authenticated user dict
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Authenticate user status on mount
  useEffect(() => {
    const checkUserAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (e) {
        // Not logged in or expired token
        setUser(null);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkUserAuth();
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    if (!window.confirm("Confirm admin logout?")) return;
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
    } catch (e) {
      console.error("Error logging out", e);
    }
  };

  return (
    <div className="App flex flex-col min-h-screen">
      <BrowserRouter>
        {/* Persistent Sticky Glass Navbar */}
        <RouteTracker />
        <Navbar isLoggedIn={!!user} onLogout={handleLogout} />

        <main className="flex-grow">
          <Routes>
            {/* Landing page */}
            <Route path="/" element={<LandingPage />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* Admin Blog management (Protected) */}
            <Route
              path="/admin/blog"
              element={
                user ? (
                  <BlogAdmin />
                ) : (
                  <LoginPage onLoginSuccess={handleLoginSuccess} />
                )
              }
            />

            {/* Admin Login page */}
            <Route 
              path="/admin/login" 
              element={<LoginPage onLoginSuccess={handleLoginSuccess} />} 
            />

            {/* Admin Dashboard page (Protected) */}
            <Route 
              path="/admin" 
              element={
                user ? (
                  <AdminDashboard />
                ) : (
                  <LoginPage onLoginSuccess={handleLoginSuccess} />
                )
              } 
            />
          </Routes>
        </main>

        {/* Footer & General Disclaimer */}
        <footer className="bg-[#0F172A] text-slate-400 border-t border-slate-800 text-left">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
              
              {/* Brand Col */}
              <div className="md:col-span-5 space-y-4">
                <div className="flex items-center space-x-3 text-white">
                  <div className="bg-[#D4AF37] p-2 rounded-lg flex items-center justify-center shadow-md">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-bold text-lg tracking-tight text-white uppercase">Ashish Ranade</span>
                </div>
                <p className="text-sm font-light leading-relaxed max-w-sm">
                  Empowering households, entrepreneurs, and families with institutional-grade financial strategies and direct educational consulting.
                </p>
              </div>

              {/* Contacts Col */}
              <div className="md:col-span-4 space-y-4">
                <h4 className="text-white font-bold text-xs uppercase tracking-widest text-[#D4AF37]">Direct Contact Details</h4>
                <ul className="space-y-3 text-sm font-light">
                  <li className="flex items-center space-x-3">
                    <User className="h-4.5 w-4.5 text-slate-500 shrink-0" />
                    <span>Ashish Ranade</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <ShieldCheck className="h-4.5 w-4.5 text-slate-500 shrink-0" />
                    <span>AMFI-Registered Mutual Fund Distributor · ARN-107338</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Phone className="h-4.5 w-4.5 text-slate-500 shrink-0" />
                    <a href="tel:9687944888" className="hover:text-white transition-colors">9687944888</a>
                  </li>
                  <li className="flex items-center space-x-3">
                    <Mail className="h-4.5 w-4.5 text-slate-500 shrink-0" />
                    <a href="mailto:ranadeashish1971@gmail.com" className="hover:text-white transition-colors">ranadeashish1971@gmail.com</a>
                  </li>
                  <li className="flex items-center space-x-3">
                    <MapPin className="h-4.5 w-4.5 text-slate-500 shrink-0" />
                    <span>Wealth Educator · Ahmedabad · Online Saturday Sessions</span>
                  </li>
                </ul>
              </div>

              {/* Disclaimer Col */}
              <div className="md:col-span-3 space-y-4">
                <h4 className="text-white font-bold text-xs uppercase tracking-widest text-[#D4AF37]">General Disclaimer</h4>
                <p className="text-xs leading-relaxed font-light text-slate-500 border-l-2 border-[#D4AF37]/50 pl-3.5 italic">
                  &quot;All training and sessions are purely educational and do not constitute investment, tax, or financial advice. Ashish Ranade does not manage or hold client funds. All investments carry risk — please consult a SEBI-registered adviser before making financial decisions.&quot;
                </p>
                <p className="text-[11px] leading-relaxed font-light text-slate-600">
                  AMFI-Registered Mutual Fund Distributor (ARN-107338). Mutual fund investments are subject to market risks; read all scheme related documents carefully.
                </p>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-800 text-center text-xs text-slate-600 flex flex-col sm:flex-row sm:justify-between gap-4 font-light">
              <span>© {new Date().getFullYear()} Ashish Ranade – Wealth Educator. All rights reserved.</span>
              <div className="flex justify-center space-x-6">
                <Link to="/admin" className="hover:text-slate-400 transition-colors">Admin Area</Link>
                <span>|</span>
                <span className="text-slate-600">Conflict-Free Education</span>
              </div>
            </div>
          </div>
        </footer>
        <WhatsAppButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
 App;
